import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Card,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconBulb, IconInfoCircle } from "@tabler/icons-react";
import api from "@/lib/api-client";

type Cluster = {
  representative: string;
  size: number;
  spaceSlug: string;
  langs: string[];
  examples: string[];
};

/**
 * Iter 116: admin panel listing top knowledge-gap clusters across
 * spaces in the workspace. Backed by GET /ee/ai/ask/knowledge-gaps —
 * pulls refusal / low-confidence questions of the period, clusters by
 * cosine similarity, returns top N. Drives "topics with missing docs"
 * — direct input for the docs team.
 */
export default function AskAiKnowledgeGaps() {
  const [days, setDays] = useState<string>("7");
  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErr(null);
    api
      .get<{ period_days: number; clusters: Cluster[] }>(
        `/ee/ai/ask/knowledge-gaps?days=${days}&topK=20`,
      )
      .then(({ data }) => {
        if (cancel) return;
        setClusters(data.clusters || []);
      })
      .catch((e) => {
        if (cancel) return;
        setErr(e?.response?.data?.message || e?.message || "Failed to load");
      })
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [days]);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={4}>Knowledge gaps</Title>
          <Text c="dimmed" size="sm">
            Topics where users keep getting refusals or low-confidence
            answers. Surfaced from clustered refusals — use as input for
            new docs.
          </Text>
        </div>
        <Select
          label="Period"
          value={days}
          onChange={(v) => setDays(v || "7")}
          data={[
            { value: "7", label: "7 days" },
            { value: "30", label: "30 days" },
            { value: "60", label: "60 days" },
            { value: "90", label: "90 days" },
          ]}
          w={150}
        />
      </Group>
      {loading && <Loader size="sm" />}
      {err && (
        <Alert color="red" icon={<IconInfoCircle />}>
          {err}
        </Alert>
      )}
      {!loading && !err && clusters.length === 0 && (
        <Alert color="gray" icon={<IconBulb />}>
          No gaps detected in the selected period — everything Ask AI
          gets asked is currently covered by the docs.
        </Alert>
      )}
      {clusters.map((c, i) => (
        <Card withBorder key={`${c.spaceSlug}:${i}`} radius="md">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Group gap="xs">
                <Badge variant="light" color="orange">
                  {c.size}×
                </Badge>
                <Badge variant="outline" color="blue">
                  {c.spaceSlug}
                </Badge>
                {c.langs.map((l) => (
                  <Badge variant="dot" key={l}>
                    {l}
                  </Badge>
                ))}
              </Group>
            </Group>
            <Text fw={600}>{c.representative}</Text>
            {c.examples.length > 0 && (
              <Stack gap={4}>
                <Text size="xs" c="dimmed">
                  Also asked as:
                </Text>
                {c.examples.map((ex, j) => (
                  <Text size="sm" c="dimmed" key={j}>
                    • {ex}
                  </Text>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
